import React, { useCallback, useEffect, useState } from 'react';
import LoadingSpinner from '../common/LoadingSpinner';
import { getPlatformInvoices, markPlatformInvoicePaid } from '../../services/api';
import { formatDateTime, formatMoney } from '../../utils/formatters';

const INVOICE_STATUS_LABELS = { pending: 'Ожидает оплаты', paid: 'Оплачен', canceled: 'Отменён' };

// Счета на оплату по безналу — авто-сверки с банком нет (см. план), поэтому
// «Отметить оплаченным» — единственный способ активировать организацию по
// этому каналу. По структуре — копия PaymentsCard (фильтр статуса + лента).
const InvoicesCard = ({ orgs = [], planById = {} }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('pending');
  const [markingId, setMarkingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getPlatformInvoices({ status: status || undefined });
      setInvoices(data.invoices || []);
      setError('');
    } catch (e) {
      setError('Не удалось загрузить счета');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const handleMarkPaid = async (invoice) => {
    if (!window.confirm(`Подтвердить, что счёт № ${invoice.number} (${formatMoney(invoice.amount)}) реально оплачен?`)) return;
    setMarkingId(invoice.id);
    try {
      await markPlatformInvoicePaid(invoice.id);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Не удалось подтвердить оплату');
    } finally {
      setMarkingId(null);
    }
  };

  return (
    <section className="ui-card">
      <div className="ui-card-head platform-card-head-row">
        <h2>Счета на оплату</h2>
        <div className="platform-filters">
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Все статусы</option>
            <option value="pending">Ожидают оплаты</option>
            <option value="paid">Оплаченные</option>
            <option value="canceled">Отменённые</option>
          </select>
        </div>
      </div>

      {loading && <LoadingSpinner />}
      {!loading && error && <div className="ui-inline-error">{error}</div>}
      {!loading && !error && invoices.length === 0 && (
        <div className="ui-empty">Счетов по этим условиям нет</div>
      )}

      {!loading && !error && invoices.length > 0 && (
        <div className="platform-org-table">
          {invoices.map((inv) => (
            <div className="platform-org-row" key={inv.id}>
              <div className="platform-org-main">
                <div className="platform-org-name">{inv.org_name || '—'} · № {inv.number}</div>
                <div className="ui-dim">
                  {(planById[inv.plan_id]?.name) || `тариф #${inv.plan_id}`} · выставлен {formatDateTime(inv.created_at)}
                  {inv.paid_at && ` · оплачен ${formatDateTime(inv.paid_at)}`}
                  {inv.promo_code && ` · промокод ${inv.promo_code}`}
                </div>
              </div>
              <span className="platform-payment-amount">{formatMoney(inv.amount)}</span>
              <span className={`ui-status ${inv.status === 'paid' ? 'on' : inv.status === 'canceled' ? 'off' : ''}`}>
                {INVOICE_STATUS_LABELS[inv.status] || inv.status}
              </span>
              {inv.status === 'pending' && (
                <div className="platform-org-actions">
                  <button
                    type="button" className="ui-btn-primary"
                    disabled={markingId === inv.id}
                    onClick={() => handleMarkPaid(inv)}
                  >
                    {markingId === inv.id ? 'Подтверждаем…' : 'Отметить оплаченным'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default InvoicesCard;
